import { Radio } from '@headlessui/react'
import { cn } from '../../utils'

export interface RadioChipProps {
  value: string
  label: React.ReactNode
  disabled?: boolean
  readOnly?: boolean
  className?: string
  testId?: string
  hasError?: boolean
  variant?: 'chip' | 'plain'
  isStatic?: boolean
  isChecked?: boolean
}

export default function RadioChip({
  value,
  label,
  disabled = false,
  readOnly = false,
  className,
  testId,
  hasError = false,
  variant = 'chip',
  isStatic = false,
  isChecked = false
}: Readonly<RadioChipProps>) {
  const getClassName = ({
    focus,
    checked,
    disabled: optionDisabled
  }: {
    focus: boolean
    checked: boolean
    disabled: boolean
  }) =>
    cn(
      'inline-block rounded transition-colors',
      {
        'select-text': variant === 'plain',
        'select-none p-0.25 cursor-default': variant !== 'plain' && readOnly,
        'select-none p-0.25 cursor-pointer': variant !== 'plain' && !readOnly
      },
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-75 dark:text-cool-gray-200',
      {
        'text-white bg-cool-gray-600 dark:bg-cool-gray-500':
          checked && !optionDisabled && variant === 'chip',
        'hover:bg-cool-gray-700':
          checked && !optionDisabled && variant === 'chip' && !readOnly,
        'text-cool-gray-700 bg-cool-gray-300':
          focus && !checked && !optionDisabled && variant === 'chip',
        'hover:bg-cool-gray-400':
          focus &&
          !checked &&
          !optionDisabled &&
          variant === 'chip' &&
          !readOnly,
        'text-cool-gray-700 bg-cool-gray-200 dark:bg-cool-gray-600':
          !checked && !focus && !optionDisabled && variant === 'chip',
        'hover:bg-cool-gray-300':
          !checked &&
          !focus &&
          !optionDisabled &&
          variant === 'chip' &&
          !readOnly,
        'text-cool-gray-700': variant === 'plain' && !checked,
        'text-cool-gray-900 font-medium': variant === 'plain' && checked,
        'text-cool-gray-500 bg-cool-gray-300 !cursor-not-allowed opacity-40 pointer-events-none':
          optionDisabled && variant === 'chip',
        'ring-2 ring-red-500': hasError && !optionDisabled
      },
      className
    )

  if (isStatic) {
    return (
      <span
        className={getClassName({
          focus: false,
          checked: isChecked,
          disabled
        })}
        data-testid={testId}
      >
        <span data-testid={testId ? `${testId}-label` : undefined}>
          {label}
        </span>
      </span>
    )
  }

  return (
    <Radio
      value={value}
      disabled={disabled}
      className={getClassName}
      data-testid={testId}
    >
      <span data-testid={testId ? `${testId}-label` : undefined}>{label}</span>
    </Radio>
  )
}
